'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Drawer, DrawerBody } from '@/ui/drawer/drawer/Drawer.client'
import { Picture } from '@/ui/media/picture/Picture.client'
import { generatePictureSource } from '@/ui/media/picture/Picture.service'
import { Title } from '@/ui/text/title/Title'
import type { EcoPart, Image } from '@gql-generated/graphql'
import { useAtom } from 'jotai'
import { displayEcoTaxesDrawerAtom } from '../../services/product.store'

type EcoTaxDetailsType = {
  ecoTax: 'deee' | 'furnishing' | 'pmcb'
  logo?: Image
}

function EcoTaxDetails({ ecoTax, logo }: EcoTaxDetailsType): React.ReactNode {
  const { t, Trans } = useTranslation(['ecoTaxesDrawer'])

  return (
    <div className="flex flex-col gap-1">
      <Title size="2xl" className="font-bold">
        {t(`ecoTaxesDrawer:${ecoTax}.title`)}
      </Title>
      {logo && <Picture image={logo} />}
      <Trans i18nKey={`ecoTaxesDrawer:${ecoTax}.description`} />
    </div>
  )
}

type EcoTaxesDrawerType = {
  ecoTaxes: EcoPart
}

export function EcoTaxesDrawer({ ecoTaxes }: EcoTaxesDrawerType): React.ReactNode {
  const { t, Trans } = useTranslation(['ecoTaxesDrawer'])
  const [isDrawerOpen, setIsDrawerOpen] = useAtom(displayEcoTaxesDrawerAtom)

  return (
    <Drawer size="xl" isOpen={isDrawerOpen} onOpenChange={() => setIsDrawerOpen(false)}>
      <DrawerBody>
        <div className="flex flex-col gap-3">
          <Trans i18nKey="ecoTaxesDrawer:description" />
          {ecoTaxes.deee && (
            <EcoTaxDetails
              ecoTax="deee"
              logo={generatePictureSource({
                desktopUrl: '/media/image/eco-taxes/deee.png',
                alt: t('ecoTaxesDrawer:deee.title'),
              })}
            />
          )}
          {ecoTaxes.furnishing && <EcoTaxDetails ecoTax="furnishing" />}
          {ecoTaxes.pmcb && (
            <EcoTaxDetails
              ecoTax="pmcb"
              logo={generatePictureSource({
                desktopUrl: '/media/image/eco-taxes/pmcb.png',
                alt: t('ecoTaxesDrawer:pmcb.title'),
              })}
            />
          )}
        </div>
      </DrawerBody>
    </Drawer>
  )
}
